import { Dialog, DialogBody, DialogTitle } from "components/Theme/dialog";
import { Form, Formik, type FormikHelpers, type FormikValues } from "formik";
import type React from "react";
import type { AnyObjectSchema } from "yup";
import DefaultDialogActionButtons from "./DefaultDialogActionButtons.tsx";

interface Props<TValues extends FormikValues = FormikValues> {
  buttonLabel: string;
  closeLabel?: string;
  title: string;
  body: string | React.ReactElement;
  className?: string;
  show: boolean;
  disabled?: boolean;
  handleClose: () => void;
  handleAction: () => void;
  // Optional Formik props
  initialValues?: TValues;
  validationSchema?: AnyObjectSchema;
  onSubmit?: (
    values: TValues,
    formikHelpers: FormikHelpers<TValues>,
  ) => void | Promise<void>;
}

const ConfirmModal = <TValues extends FormikValues = FormikValues>(
  props: Props<TValues>,
): React.ReactElement => {
  // If Formik props provided, wrap with Formik
  if (props.initialValues && props.onSubmit) {
    return (
      <Dialog
        open={props.show}
        className={props.className}
        onClose={props.handleClose}
      >
        <Formik
          initialValues={props.initialValues}
          validationSchema={props.validationSchema}
          onSubmit={props.onSubmit}
          enableReinitialize={true}
        >
          {({ resetForm }) => (
            <Form>
              <DialogTitle>{props.title}</DialogTitle>
              <DialogBody>{props.body}</DialogBody>
              <DefaultDialogActionButtons
                createButton={{
                  disabled: props.disabled,
                  labelText: props.buttonLabel,
                  type: "submit",
                }}
                cancelButton={{
                  onClick: () => {
                    resetForm();
                    props.handleClose();
                  },
                  labelText: props.closeLabel,
                  type: "reset",
                }}
              />
            </Form>
          )}
        </Formik>
      </Dialog>
    );
  }

  // Default non-Formik mode
  return (
    <>
      <Dialog
        open={props.show}
        className={props.className}
        onClose={props.handleClose}
      >
        <DialogTitle>{props.title}</DialogTitle>
        <DialogBody>{props.body}</DialogBody>
        <DefaultDialogActionButtons
          createButton={{
            disabled: props.disabled,
            onClick: props.handleAction,
            labelText: props.buttonLabel,
            type: "submit",
          }}
          cancelButton={{
            onClick: props.handleClose,
            labelText: props.closeLabel,
            type: "reset",
          }}
        />
      </Dialog>
    </>
  );
};

export default ConfirmModal;
